import init from '../../../js/init';
import { fillZeros, getURLParams, kebabToCamel, setURLParams } from '../../../js/utils';

const DEFAULT_REFRESH_INTERVAL = 5 * 60 * 1000; // 5 min in ms. How often data needs to be refreshed.

const name = 'flights';
const selector = '.flights';
const formSelector = `${selector} form`;
const tableSelector = `${selector}__table`;
const tbodySelector = 'table tbody';
const accordionSelector = `${selector}__accordion`;
const accordionItemsSelector = `${selector}__accordion-items`;
const filtersSelector = `${selector}__filters`;
const errorSelector = `${selector}__error_container`;
const filterSelector = `${selector}__filter [name]`;
const showMoreSelector = `${selector}__show_more__button`;
const loaderSelector = `${selector}__loader`;
const itemHiddenClass = `${selector}__item--hidden`.substring(1);
const infoStatusSelector = `${selector}__status--info`;
const successStatusSelector = `${selector}__status--success`;
const earlyStatusSelector = `${selector}__status--early`;
const warningStatusSelector = `${selector}__status--warning`;
const errorStatusSelector = `${selector}__status--error`;

const FILTER_COMPARISONS = {
  contains: 0,
  equals: 1,
  notEquals: 2,
  greaterThan: 3,
  lessThan: 4,
  greaterOrEquals: 5,
  lessOrEquals: 6,
};

class Flights {
  constructor(root, columns, api, direction, refreshInterval, filterValues) {
    const table = root.querySelector(tableSelector);
    const tbody = table.querySelector(tbodySelector);
    const accordion = root.querySelector(accordionSelector);
    const accordionItems = accordion.querySelector(accordionItemsSelector);
    const filters = root.querySelector(filtersSelector);
    const filterElems = filters.querySelectorAll(filterSelector);
    const showMoreElem = root.querySelector(showMoreSelector);
    const loaderElem = root.querySelector(loaderSelector);
    const infoStatus = root.querySelector(infoStatusSelector);
    const successStatus = root.querySelector(successStatusSelector);
    const earlyStatus = root.querySelector(earlyStatusSelector);
    const warningStatus = root.querySelector(warningStatusSelector);
    const errorStatus = root.querySelector(errorStatusSelector);
    const errorContainer = root.querySelector(errorSelector);

    this.filtersData = {};
    this.filterComparisons = {};
    this.items = [];
    this.filters = filters;
    this.filterElems = filterElems;
    this.showMoreButton = showMoreElem;
    this.loader = loaderElem;
    this.columns = columns;
    this.api = api;
    this.direction = direction;
    this.table = table;
    this.tbodyElem = tbody;
    this.accordion = accordion;
    this.accordionItemsElem = accordionItems;
    this.errorMessage = errorContainer;
    this.infoStatus = infoStatus;
    this.successStatus = successStatus;
    this.earlyStatus = earlyStatus;
    this.warningStatus = warningStatus;
    this.errorStatus = errorStatus;
    this.refreshInterval = +refreshInterval || DEFAULT_REFRESH_INTERVAL;
    this.form = root.querySelector(formSelector);
    this.offsetCounter = 0;

    this.initFilters(filterValues);
    this.initItems();
    this.setItemsInterval();
    this.updateFilterFieldValues();
    this.listenToFilterChanges();
  }

  getElementData(elem) {
    const data = {};
    [...elem.attributes].forEach(({ name: attrName, value }) => {
      if (attrName.startsWith('data-')) data[kebabToCamel(attrName.replace('data-', ''))] = value;
    });

    return data;
  }

  createRow(data) {
    const row = document.createElement('tr');

    for (const { key } of this.columns) {
      const td = document.createElement('td');
      if (key === 'status') td.innerHTML = this.renderStatus(data[key]);
      else if (key === 'airline_name') {
        td.innerHTML = this.renderAirline(data[key], data.airline_logo);
        td.classList.add('flights__airline-cell');
      } else if (key === 'schedule_time') {
        td.innerHTML = this.renderTime(data[key], data.status, data.estimate_time);
        td.classList.add('font-bold');
      } else {
        td.innerHTML = data[key];
      }
      row.appendChild(td);
    }

    this.tbodyElem.appendChild(row);

    return row;
  }

  createAccordionItem(data) {
    const accordionItem = document.createElement('div');
    accordionItem.className = 'accordion-item w-full relative';
    
    const id = `flight-${data.flightno}-${Date.now()}`;
    
    // Create the accordion header (collapsed state)
    const headerContent = `
      <div class="flex items-start justify-between w-full">
        <div class="flex flex-col">
          <div class="text-lg font-bold">${this.renderTime(data.schedule_time, data.status, data.estimate_time)}</div>
          <div class="text-sm text-gray-600">${data.month_day}</div>
        </div>
        <div class="flex flex-col items-start" style="width: 66%;">
          <div class="text-sm text-gray-600 font-bold">${data.origin}</div>
          <div class="text-sm text-gray-600">${data.flightno}</div>
          ${this.renderStatus(data.status)}
        </div>
      </div>
    `;

    // Create the accordion content (expanded state)
    const expandedContent = `
      <div class="grid grid-cols-2 gap-4 py-4 px-2 text-sm">
        <div>
          <div class="font-bold text-gray-700">Time</div>
          <div>${data.month_day}</div>
          <div>${this.renderTime(data.schedule_time, data.status, data.estimate_time)}</div>
        </div>
        <div>
          <div class="font-bold text-gray-700">${this.direction == 'Arrivals' ? 'Origin' : 'Destination'}</div>
          <div>${data.origin}</div>
        </div>
        <div>
          <div class="font-bold text-gray-700">Airline</div>
          <div>${data.airline_name}</div>
        </div>
        <div>
          <div class="font-bold text-gray-700">Flight No.</div>
          <div>${data.flightno}</div>
        </div>
        <div>
          <div class="font-bold text-gray-700">Gate</div>
          <div>${data.gate}</div>
        </div>
        <div>
          <div class="font-bold text-gray-700">Terminal</div>
          <div>${data.terminal}</div>
        </div>
      </div>
    `;

    accordionItem.innerHTML = `
      <input class="accordion-item__input sr-only" id="${id}" type="checkbox" name="accordion-item">
      <div aria-hidden="true" class="accordion-item__border transition-colors duration-300 absolute w-full h-full left-0 top-0 pointer-events-none border-b border-gray-70"></div>
      <label class="flex items-center cursor-pointer transition-padding duration-300 gap-x-2 px-2.5 py-2" for="${id}">
        <span class="accordion-item__label flex-1">
          ${headerContent}
        </span>
        <div class="accordion-item__arrow ml-auto transition duration-300 text-secondary-90 w-3 h-3 rotate-90 self-start">
          <svg viewBox="0 0 24 24" fill="currentColor" class="w-full h-full">
            <path d="M8.59 16.59L13.17 12 8.59 7.41 10 6l6 6-6 6-1.41-1.41z"/>
          </svg>
        </div>
      </label>
      <div class="accordion-item__content overflow-hidden max-h-0 transition-[max-height] duration-300">
        <div class="pb-2 accordion-item__content-inner prose">
          ${expandedContent}
        </div>
      </div>
    `;

    this.accordionItemsElem.appendChild(accordionItem);

    return accordionItem;
  }

  initFilters(filterValues = {}) {
    this.filterElems.forEach(filter => {
      const defaultComparison = filter.type == 'time'
      ? FILTER_COMPARISONS.greaterOrEquals : FILTER_COMPARISONS.contains;

      const filterComparison = filter.getAttribute('data-comparison') ?
        FILTER_COMPARISONS[filter.getAttribute('data-comparison')] :
        defaultComparison;

      this.filtersData[filter.name] = {
        type: filter.type,
        comparison: filterComparison,
        value: filterValues[filter.name],
      }
    });
  }

  async initItems() {
    const rowsData = await this.getData().catch(e => {
      console.error(e);
      this.loader.classList.add('hidden');
      this.table.classList.remove('flights-loaded');
      this.accordion.classList.remove('flights-loaded');
      this.errorMessage.classList.remove('hidden');
    });
    if (!rowsData) {
      this.table.classList.remove('flights-loaded');
      this.accordion.classList.remove('flights-loaded');
      this.errorMessage.classList.remove('hidden');
      this.loader.classList.add('hidden');
      return;
    }

    this.table.classList.add('flights-loaded');
    this.accordion.classList.add('flights-loaded');
    this.errorMessage.classList.add('hidden');
    this.items = rowsData.map(data => ({
      tableElem: this.createRow(data),
      accordionElem: this.createAccordionItem(data),
      data: { ...data, search: this.combineData(data) },
    }));

    this.filterItems();
    this.updateFilterFieldValues();
    this.loader.classList.add('hidden');
  }

  async refreshItems() {
    let i = 0;
    let previousCount = 0;
    for (; i <= this.offsetCounter; i++) {
      const rowsData = await this.getData(i).catch();
      if (!rowsData) {
        // Instead of removing valid data, we just ignore the failure.
        return;
      }

      this.table.classList.remove('hidden');
      this.errorMessage.classList.add('hidden');
      this.items.splice(previousCount, rowsData.length, ...rowsData);
      previousCount = previousCount + rowsData.length;

      this.filterItems();
      this.updateFilterFieldValues();
    }
  }

  async addItems() {
    this.loader.classList.remove('hidden');
    const rowsData = await this.getData().catch(e => {
      console.error(e);
      this.loader.classList.add('hidden');
      this.table.classList.remove('flights-loaded');
      this.accordion.classList.remove('flights-loaded');
      this.errorMessage.classList.remove('hidden');
    });
    if (!rowsData) {
      this.loader.classList.add('hidden');
      this.table.classList.remove('flights-loaded');
      this.accordion.classList.remove('flights-loaded');
      this.errorMessage.classList.remove('hidden');
      return;
    }

    // Create new items with the search property
    const newItems = rowsData.map(data => {
      // Add the combined search field to each item
      const enhancedData = {
        ...data,
        search: this.combineData(data)
      };

      // Create both table row and accordion item
      return {
        tableElem: this.createRow(enhancedData),
        accordionElem: this.createAccordionItem(enhancedData),
        data: enhancedData
      };
    });

    // Properly add the new items to the items array
    this.items = this.items.concat(newItems);

    // Apply filtering to all items (including new ones)
    this.filterItems();

    this.updateFilterFieldValues();
    this.loader.classList.add('hidden');
  }

  async getData(offset) {
    const api_with_offset = this.api + '?offset=' + (offset !== undefined ? offset.toString() : this.offsetCounter.toString());
    const res = await fetch(api_with_offset);
    if (!res.ok) {
      const message = `An error has occured: ${res.status}`;
      throw new Error(message);
    }
    const body = await res.json();
    return body.data;
  }

  combineData(data) {
    return Object.values(data).join(' ');
  }

  renderStatus(statusText) {
    const statusLower = statusText.trim().toLowerCase();

    let statusElem = this.warningStatus;
    if (statusLower.includes('arrived') || statusLower.includes('departed')) statusElem = this.successStatus;
    else if (statusLower.includes('early')) statusElem = this.earlyStatus;
    else if (statusLower.includes('on time')) statusElem = this.infoStatus;
    else if (statusLower.includes('canceled')) statusElem = this.errorStatus;

    statusElem = statusElem.cloneNode(true);

    statusElem.querySelector('span').innerHTML = statusText;

    return statusElem.innerHTML;
  }

  renderTime(scheduleTime, status, estimateTime) {
    const statusLower = status ? status.trim().toLowerCase() : '';
    
    // Check if flight is late and has an estimate time
    if (statusLower.includes('late') && estimateTime) {
      return `
        <div class="flights__time-container">
          <div class="flights__time-original" style="font-weight: 400; text-decoration: line-through;">${scheduleTime}</div>
          <div class="flights__time-estimate" style="color: red">${estimateTime}</div>
        </div>
      `;
    }
    
    // Return normal time display for non-late flights
    return scheduleTime;
  }

  renderAirline(airline, airlineLogo) {
    return `
      <div class="flex items-center gap-1.5 h-8">
        <div class="h-full w-[9.5rem] px-1.5 bg-gray-50 flex items-center">
          <img src="${airlineLogo}" class="inline h-2 w-full object-contain" loading="lazy" />
        </div>
        <span>${airline}</span>
      </div>
    `;
  }

  setItemsInterval() {
    setInterval(this.refreshItems.bind(this), this.refreshInterval);
  }

  filterItems() {
    this.items.forEach(item => {
      const { tableElem, accordionElem, data } = item;
      let hidden = false;

      for (const key in this.filtersData) {
        if (Object.hasOwn(this.filtersData, key)) {
          const filter = this.filtersData[key];
          if (!filter.value) continue;
          const filterValue = filter.value.trim().toLowerCase();
          let itemValue = data[key] && data[key].trim().toLowerCase();

          if (!filterValue || !itemValue) continue;

          if (filter.type === 'time') {
            let hours = itemValue.split(':')[0];
            let minutes = itemValue.split(':')[1].substring(0, 2);
            let suffix = itemValue.substring(itemValue.length - 2);

            hours = +hours;
            minutes = +minutes;

            if (suffix.toLowerCase() === 'pm' && hours !== 12)
              hours += 12

            itemValue = `${fillZeros(hours)}:${fillZeros(minutes)}`;
          }

          if (
            filter.comparison == FILTER_COMPARISONS.contains && !itemValue.includes(filterValue) ||
            filter.comparison === FILTER_COMPARISONS.equals && itemValue !== filterValue ||
            filter.comparison === FILTER_COMPARISONS.greaterThan && itemValue <= filterValue ||
            filter.comparison === FILTER_COMPARISONS.lessThan && itemValue >= filterValue ||
            filter.comparison === FILTER_COMPARISONS.greaterOrEquals && itemValue < filterValue ||
            filter.comparison === FILTER_COMPARISONS.lessOrEquals && itemValue > filterValue
          ) {
            hidden = true;
            break;
          }
        }
      }

      item.hidden = hidden;
      
      // Apply hidden class to both table row and accordion item
      if (hidden) {
        if (tableElem && tableElem.classList) {
          tableElem.classList.add(itemHiddenClass);
        }
        if (accordionElem && accordionElem.classList) {
          accordionElem.classList.add(itemHiddenClass);
        }
      } else {
        if (tableElem && tableElem.classList) {
          tableElem.classList.remove(itemHiddenClass);
        }
        if (accordionElem && accordionElem.classList) {
          accordionElem.classList.remove(itemHiddenClass);
        }
      }
    });
  }

  updateFilterValues() {
    this.filterElems.forEach(filter => {
      this.filtersData[filter.name].value = filter.value;
    });

    const vals = {};
    Object.keys(this.filtersData).forEach(key => vals[key] = this.filtersData[key].value);

    setURLParams(vals);
  }

  updateFilterFieldValues() {
    this.filterElems.forEach(filter => {
      if (
        this.filtersData[filter.name] && this.filtersData[filter.name].value !== undefined &&
        filter.value !== this.filtersData[filter.name]
      )
        filter.value = this.filtersData[filter.name].value;
    });
  }

  listenToFilterChanges() {
    this.filters.addEventListener('input', () => {
      this.updateFilterValues();
      this.filterItems();
    });
    this.form.addEventListener('change', () => {
      this.updateFilterValues();
      this.filterItems();
    });
    this.showMoreButton.addEventListener('click', () => {
      this.offsetCounter = this.offsetCounter + 1;
      this.addItems();
    })
  }
}

init(Drupal, name, selector, (root, data) => {
  const filterValues = getURLParams();

  new Flights(root, data.columns, data.api, data.direction, data.refreshInterval, filterValues);
});
